'use client';

import { useEffect } from 'react';

/**
 * TEMPORARY DIAGNOSTIC — remove once the hydration mismatch is resolved.
 *
 * Decodes minified React errors (production builds only emit a code + args)
 * and reports what the server rendered vs what the client rendered.
 */

// Verbatim from facebook/react scripts/error-codes/codes.json
const REACT_ERROR_MESSAGES: Record<string, string> = {
  '418':
    "Hydration failed because the server rendered %s didn't match the client. As a result this tree will be regenerated on the client. This can happen if a SSR-ed Client Component used:\n\n- A server/client branch `if (typeof window !== 'undefined')`.\n- Variable input such as `Date.now()` or `Math.random()` which changes each time it's called.\n- Date formatting in a user's locale which doesn't match the server.\n- External changing data without sending a snapshot of it along with the HTML.\n- Invalid HTML tag nesting.\n\nIt can also happen if the client has a browser extension installed which messes with the HTML before React loaded.\n\nhttps://react.dev/link/hydration-mismatch%s",
  '419':
    'The server could not finish this Suspense boundary, likely due to an error during server rendering. Switched to client rendering.',
  '421':
    'This Suspense boundary received an update before it finished hydrating. This caused the boundary to switch to client rendering. The usual way to fix this is to wrap the original update in startTransition.',
  '422':
    'There was an error while hydrating but React was able to recover by instead client rendering from the nearest Suspense boundary.',
  '423':
    'There was an error while hydrating but React was able to recover by instead client rendering the entire root.',
  '424':
    'This root received an early update, before anything was able hydrate. Switched the entire root to client rendering.',
  '425': 'Text content does not match server-rendered HTML.',
};

type Snapshot = {
  bodyChildren: number;
  elementsWithClass: number;
  inputs: number;
  bodyAttributes: string[];
  htmlPreview: string;
  appElements: number;
};

const NON_APP_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE', 'TEMPLATE', 'NOSCRIPT']);

// Markup the app itself owns, ignoring bootstrap scripts and the hidden
// metadata placeholder Next.js streams into <body>.
const countAppElements = () =>
  Array.from(document.body.querySelectorAll('*')).filter(
    (el) => !NON_APP_TAGS.has(el.tagName) && !el.closest('div[hidden]')
  ).length;

const takeSnapshot = (): Snapshot => ({
  bodyChildren: document.body.children.length,
  elementsWithClass: document.body.querySelectorAll('[class]').length,
  inputs: document.body.querySelectorAll('input, textarea').length,
  bodyAttributes: Array.from(document.body.attributes).map((a) => a.name),
  htmlPreview: document.body.innerHTML.slice(0, 1500),
  appElements: countAppElements(),
});

// Client component modules are evaluated before hydrateRoot runs, so the DOM
// here is still exactly what the server sent.
const serverSnapshot: Snapshot | null = typeof document === 'undefined' ? null : takeSnapshot();

const decodeReactError = (input: unknown) => {
  const raw = input instanceof Error ? `${input.message}` : String(input ?? '');
  const codeMatch = /Minified React error #(\d+)/.exec(raw);
  if (!codeMatch) {
    return null;
  }

  const code = codeMatch[1];
  const args = Array.from(raw.matchAll(/args\[\]=([^&\s]*)/g)).map((m) => {
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  });

  let text = REACT_ERROR_MESSAGES[code] ?? `Unmapped React error #${code}`;
  args.forEach((arg) => {
    text = text.replace('%s', arg);
  });
  text = text.replace(/%s/g, '');

  return { code, args, text, raw };
};

declare global {
  interface Window {
    __reactErrorDiagnosticsInstalled?: boolean;
    __reactHydrationErrors?: { code: string; text: string }[];
  }
}

const HYDRATION_CODES = new Set(['418', '419', '421', '422', '423', '424', '425']);

// Only patch in production: dev builds already print full React messages, and
// wrapping console.error makes every error look like it originates here.
if (
  process.env.NODE_ENV === 'production' &&
  typeof window !== 'undefined' &&
  !window.__reactErrorDiagnosticsInstalled
) {
  window.__reactErrorDiagnosticsInstalled = true;
  window.__reactHydrationErrors = [];

  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    const decoded = decodeReactError(args[0]);
    if (decoded) {
      if (HYDRATION_CODES.has(decoded.code)) {
        window.__reactHydrationErrors?.push({ code: decoded.code, text: decoded.text });
      }
      originalError('%c[react-error-decoded] #' + decoded.code, 'color:#fff;background:#c00;padding:2px 6px');
      originalError(decoded.text);
      if (decoded.args.length) {
        originalError('args[]:', decoded.args);
      }
    }
    originalError.apply(console, args as []);
  };
}

export const HydrationDiagnostics = () => {
  useEffect(() => {
    // Recoverable errors are reported asynchronously, so let the queue drain.
    const timer = setTimeout(() => {
      const clientSnapshot = takeSnapshot();
      const hydrationErrors = window.__reactHydrationErrors ?? [];

      console.group('%c[hydration-diagnostics]', 'color:#fff;background:#06c;padding:2px 6px');
      console.log('build:', process.env.NODE_ENV, '| url:', window.location.href);
      console.log('SERVER rendered (captured pre-hydration):', serverSnapshot);
      console.log('CLIENT rendered (captured post-hydration):', clientSnapshot);

      if (hydrationErrors.length) {
        console.warn(
          '%cHYDRATION ERRORS OBSERVED',
          'color:#fff;background:#c00;padding:2px 6px',
          hydrationErrors.map((e) => '#' + e.code)
        );
        hydrationErrors.forEach((e) => console.warn('#' + e.code, e.text));
      } else {
        console.log('No React hydration errors captured on this page load.');
      }

      if (serverSnapshot && serverSnapshot.appElements === 0) {
        console.warn(
          '%cSERVER PRODUCED NO APP MARKUP',
          'color:#fff;background:#c00;padding:2px 6px',
          'The server <body> contained only scripts and streamed metadata (' +
            serverSnapshot.bodyChildren +
            ' children, ' +
            serverSnapshot.appElements +
            ' app elements). Any client-rendered subtree will therefore fail to hydrate. ' +
            'Check StyledWrapper (packages/ui/components/styled-wrapper.tsx) and any other ' +
            'server/client branch above the app tree.'
        );
      } else if (serverSnapshot) {
        console.log(
          'Server produced ' +
            serverSnapshot.appElements +
            ' app elements, client has ' +
            clientSnapshot.appElements +
            '. Compare the htmlPreview fields above to locate the diverging subtree.'
        );
      }

      const extensionAttributes = (serverSnapshot?.bodyAttributes ?? []).filter((a) => a.startsWith('data-'));
      if (extensionAttributes.length) {
        console.warn('Attributes present on <body> before hydration (possible extension):', extensionAttributes);
      }

      console.groupEnd();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return null;
};
